// src/components/audio/HorenTranscript.jsx
// Transcript body for the Hören practice "Transkript" drawer (rendered inside
// SlideOutPanel). Data-driven: renders any probe part's `transcript` object.
//
// Each segment is labelled with the Aufgabe number(s) from `segment.linkedQuestionIds`,
// so learners see WHERE in the script each answer lives (no audio timing needed).
// Any probe that has a `transcript` renders automatically — supplying transcripts
// for the remaining probes (e.g. B2 002–005) needs zero extra code.
import React from 'react';
import { AlertTriangle } from 'lucide-react';

const TEIL_LABEL = {
  'news-broadcast': 'Nachrichtensendung',
  'radio-interview': 'Rundfunkinterview',
  'short-texts-announcements': 'Kurze Ansagen',
};

// speakerId → German role label. `null` = exam narrator (framing), shown muted.
function speakerLabel(speakerId) {
  if (!speakerId || speakerId === 'spk-narrator') return null;
  if (speakerId.startsWith('spk-news')) return 'Nachrichten';
  if (speakerId === 'spk-host') return 'Moderation';
  if (speakerId === 'spk-guest') return 'Gast';
  if (speakerId.startsWith('spk-ann')) return 'Durchsage';
  return 'Sprecher';
}

function sfxLabel(prompt = '') {
  const p = String(prompt).toLowerCase();
  if (/jingle|music|musik|sting|radio/.test(p)) return '♪ Jingle / Musik';
  if (/phone|telefon|freizeichen|dial|wähl|line hum/.test(p)) return '☎ Telefon';
  return '· Geräusch ·';
}

// Flatten nested `group` cues (ambience-bed wrappers) into a flat cue list.
function flattenCues(cues = []) {
  const out = [];
  for (const c of cues) {
    if (c && c.type === 'group' && Array.isArray(c.cues)) out.push(...flattenCues(c.cues));
    else if (c) out.push(c);
  }
  return out;
}

function questionNums(linkedQuestionIds = []) {
  return linkedQuestionIds
    .map((q) => parseInt(String(q).replace(/\D/g, ''), 10))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
}

function badgeLabel(nums) {
  if (!nums.length) return null;
  if (nums.length === 1) return `Aufgabe ${nums[0]}`;
  const contiguous = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
  return contiguous ? `Aufgaben ${nums[0]}–${nums[nums.length - 1]}` : `Aufgaben ${nums.join(', ')}`;
}

const CueLine = ({ cue }) => {
  if (cue.type === 'speech') {
    const label = speakerLabel(cue.speakerId);
    if (!label) {
      return <p className="text-[12px] text-gray-400 italic leading-relaxed">{cue.text}</p>;
    }
    return (
      <p className="text-[13.5px] text-gray-800 leading-relaxed">
        <span className="font-bold text-[#275eb4] mr-1.5">{label}:</span>
        {cue.text}
      </p>
    );
  }
  if (cue.type === 'sfx') {
    return <p className="text-[11px] text-gray-400 font-medium tracking-wide">{sfxLabel(cue.prompt)}</p>;
  }
  return null;
};

const CueList = ({ cues, keyPrefix }) => (
  <>
    {flattenCues(cues).map((c, i) => (
      <CueLine key={`${keyPrefix}-${i}`} cue={c} />
    ))}
  </>
);

const HorenTranscriptBody = ({ part }) => {
  const transcript = part?.transcript;

  if (!transcript) {
    return (
      <p className="text-[13px] text-gray-500">
        Für diese Probe ist noch kein Transkript verfügbar.
      </p>
    );
  }

  if (typeof transcript.rawText === 'string' && !transcript.intro && !transcript.segments && !transcript.outro) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-lg border border-amber-300/70 bg-amber-50 px-3 py-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-amber-800 font-medium leading-snug">
            Enthält die Lösungen. Höre zuerst und beantworte die Aufgaben, bevor du das Transkript liest.
          </p>
        </div>
        <pre className="whitespace-pre-wrap rounded-lg border border-black/5 bg-white p-3 text-[13px] leading-relaxed text-gray-800">
          {transcript.rawText}
        </pre>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Spoiler warning */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-300/70 bg-amber-50 px-3 py-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-[12px] text-amber-800 font-medium leading-snug">
          Enthält die Lösungen. Höre zuerst und beantworte die Aufgaben, bevor du das Transkript liest.
        </p>
      </div>

      {(part.partName || TEIL_LABEL[part.taskType]) && (
        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
          {part.partName}
          {TEIL_LABEL[part.taskType] ? ` · ${TEIL_LABEL[part.taskType]}` : ''}
        </p>
      )}

      {Array.isArray(transcript.intro) && transcript.intro.length > 0 && (
        <div className="space-y-1">
          <CueList cues={transcript.intro} keyPrefix="intro" />
        </div>
      )}

      {(transcript.segments || []).map((seg, si) => {
        const badge = badgeLabel(questionNums(seg.linkedQuestionIds));
        return (
          <div key={seg.segmentId || si} className="rounded-lg bg-white border border-black/5 shadow-sm p-3">
            {badge && (
              <div className="mb-1.5">
                <span className="inline-flex items-center rounded-full bg-[#3A86FF]/10 border border-[#3A86FF]/20 px-2 py-0.5 text-[11px] font-bold text-[#275eb4]">
                  {badge}
                </span>
              </div>
            )}
            <div className="space-y-1.5">
              <CueList cues={seg.cues} keyPrefix={`seg-${si}`} />
            </div>
          </div>
        );
      })}

      {Array.isArray(transcript.outro) && transcript.outro.length > 0 && (
        <div className="space-y-1">
          <CueList cues={transcript.outro} keyPrefix="outro" />
        </div>
      )}
    </div>
  );
};

export default HorenTranscriptBody;
